import React from 'react';
import { Box, Tooltip, alpha, useTheme } from '@mui/material';
import { PDVThemeColors } from './PDVThemes';
import { pdvScale } from './pdvScale';

interface PDVShortcutProps {
  icon: React.ElementType;
  label: string;
  shortcut?: string;
  onClick: () => void;
  disabled?: boolean;
  colors: PDVThemeColors;
  // Cor fixa por ação — sem isso, cai em colors.footerAccent (quando o tema
  // define, ver PDVThemes.ts) ou colors.primarySolid.
  iconColor?: string;
}

// Só o ícone, sem label/atalho visível embaixo (a pedido) — nome e atalho
// (ex.: "Sangria · F5") viram só o title do Tooltip, no hover, pra não
// perder a informação por completo.
export const PDVShortcut: React.FC<PDVShortcutProps> = ({
  icon: Icon, label, shortcut, onClick, disabled = false, colors, iconColor,
}) => {
  // Sem fundo escuro fixo por trás (rodapé transparente, ver PDVFooter) — o
  // tom "apagado" do ícone desabilitado precisa seguir o modo claro/escuro
  // do app, senão fica um cinza claro quase invisível sobre fundo claro.
  const theme = useTheme();
  const textoBase = theme.palette.mode === 'light' ? colors.textPrimary : (colors.barDarkText ?? colors.textOnPrimary);
  const corAtiva = iconColor ?? colors.footerAccent ?? colors.primarySolid;
  const corMuted = alpha(textoBase, 0.4);
  const corIcone = disabled ? corMuted : corAtiva;
  return (
    // Tooltip precisa de um filho que aceite ref/eventos mesmo desabilitado —
    // um <button disabled> não dispara nada, por isso o <span> por fora
    // (mesmo truque já usado em PDVFooter pro botão "Suprimento").
    <Tooltip title={shortcut ? `${label} (${shortcut})` : label}>
      <span>
        <Box
          component="button"
          type="button"
          onClick={disabled ? undefined : onClick}
          disabled={disabled}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            // Container do ícone — 52×52px em 1920×1080 (--pdv-scale).
            width: pdvScale(52),
            height: pdvScale(52),
            borderRadius: pdvScale(12),
            transition: 'background-color 150ms ease',
            WebkitTapHighlightColor: 'transparent',
            '&:hover': disabled ? undefined : {
              backgroundColor: alpha(colors.primarySolid, 0.08),
            },
            '&:hover .pdv-shortcut-icon': disabled ? undefined : {
              transform: 'translateY(-2px)',
            },
          }}
        >
          <Icon
            className="pdv-shortcut-icon"
            sx={{
              fontSize: pdvScale(48),
              color: corIcone,
              transition: 'color 150ms ease, transform 150ms ease',
            }}
          />
        </Box>
      </span>
    </Tooltip>
  );
};
